import { ModButton, ModSkeleton } from '@moderneinc/design-system-components';
import { Stack, Typography } from '@mui/material';
import type { DynamicOptionsLoadingProps } from 'next/dynamic';
import type { ReactElement } from 'react';

type LazyLoadFallbackOptions = {
  /** What failed to load, as in "The {noun} couldn't load." */
  noun: string;
  height: number | string;
};

/**
 * `loading` for next/dynamic. next/dynamic reports load failures here rather
 * than throwing, so this is the only place one can surface.
 */
// NOTE: returns a render function, not a FunctionComponent — React 19's may
// return a Promise, which `loading` does not accept (TS2322).
export const lazyLoadFallback =
  ({ noun, height }: LazyLoadFallbackOptions) =>
  ({ error }: DynamicOptionsLoadingProps): ReactElement => {
    if (!error) {
      return <ModSkeleton width="100%" height={height} />;
    }

    return (
      <Stack
        role="alert"
        sx={{
          height,
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1
        }}
      >
        <Typography variant="bodySm">
          The {noun} couldn&apos;t load. This usually means the app was updated
          while this tab was open.
        </Typography>
        {/* Not `retry`: after a deploy it re-requests the old build's chunk URL. */}
        <ModButton
          variant="secondary"
          size="small"
          onClick={() => window.location.reload()}
        >
          Reload
        </ModButton>
      </Stack>
    );
  };
